import type { IconProps } from '@magicschool/spellbook/components/Icon';
import { Icon } from '@magicschool/spellbook/components/Icon';
import { Slot } from '@magicschool/spellbook/components/Slot';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';

const adornmentSize = cva('', {
  variants: {
    size: { lg: 'my-2', md: 'my-1.25' },
    type: { leading: '', trailing: '' },
  },
  compoundVariants: [
    { class: 'ms-3 me-2', type: 'leading', size: 'lg' },
    { class: 'ms-2 me-1', type: 'leading', size: 'md' },
    { class: 'me-3 ms-2', type: 'trailing', size: 'lg' },
    { class: 'me-2 ms-1', type: 'trailing', size: 'md' },
  ],
  defaultVariants: { size: 'md' },
});

export const LeadingAdornmentSlot = Slot<
  Omit<IconProps, 'size'> & { size?: 'lg' | 'md' },
  React.ComponentProps<'div'> & { size?: 'lg' | 'md' }
>({
  displayName: 'Textarea.LeadingAdornment',
  renderOverride: ({ className, children, size, ...props }) => {
    return (
      <div className={cn('sticky end-0 top-0', adornmentSize({ size, type: 'leading' }), className)} {...props}>
        {children}
      </div>
    );
  },
  renderDefault: ({ name = 'square-dashed', size = 'md', color = 'default', className, ...props }) => (
    <div
      data-spellbook-name="textarea-leading-adornment"
      className={cn('sticky end-0 top-0', adornmentSize({ size, type: 'leading' }), className)}
    >
      <Icon name={name} size="sm" color={color} className={cn('m-1', className)} {...props} />
    </div>
  ),
});

export const TrailingAdornmentSlot = Slot<
  Omit<IconProps, 'size'> & { size?: 'lg' | 'md' },
  React.ComponentProps<'div'> & { size?: 'lg' | 'md' }
>({
  displayName: 'Textarea.TrailingAdornment',
  renderOverride: ({ className, children, size, ...props }) => {
    return (
      <div className={cn('absolute end-0 top-0', adornmentSize({ size, type: 'trailing' }), className)} {...props}>
        {children}
      </div>
    );
  },
  renderDefault: ({ name = 'square-dashed', size = 'md', color = 'default', className, ...props }) => (
    <div
      data-spellbook-name="textarea-trailing-adornment"
      className={cn('absolute end-0 top-0', adornmentSize({ size, type: 'trailing' }), className)}
    >
      <Icon name={name} size="sm" color={color} className={cn('m-1', className)} {...props} />
    </div>
  ),
});

export const AddOnSlot = Slot<React.ComponentProps<'div'>>({
  displayName: 'Textarea.AddOn',
  renderDefault: ({ className, children, ...props }) => (
    <div data-spellbook-name="textarea-add-on" className={cn('w-full', className)} {...props}>
      {children}
    </div>
  ),
});
